import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';

import { fetchGeoLocationsByIds, getGeoLocationByUrlName } from '@/api/geoService';
import { getPriceEvolution, getPriceValue } from '@/api/iaziApi';
import { locationDropdownGeoIds } from '@/components/local-landing-page/PriceDevelopment/locations';
import { AppState, PriceData } from '@/store/types';
import {
  LocalLandingLocations,
  PropertyType,
  geoIdToRouteLocationMapping,
  routeLocationToGeoIdMapping,
} from '@/types';
import { captureAction } from '@smg-real-estate/browser-rum';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { GeoLocation } from '@smg-real-estate/geo-libs';

export interface LocalLandingModule extends Module<LocalLandingState, AppState> {
  state: () => LocalLandingState;
  getters: LocalLandingGetters;
  mutations: LocalLandingMutations;
  actions: LocalLandingActions;
}

export interface LocalLandingState {
  routeLocation: LocalLandingLocations;
  selectedGeoLocationId: string;
  geoLocations: GeoLocation[];
  propertyType: PropertyType;
  isResolvingLocation: boolean; // Resolve route location / dropdown locations from Geo API
  isPriceDataLoading: boolean;
  priceData: PriceData;
  hasResolveErrors: boolean;
}

interface LocalLandingGetters extends GetterTree<LocalLandingState, AppState> {
  getRouteLocation: (state: LocalLandingState) => LocalLandingLocations;
  getGeoLocations: (state: LocalLandingState) => GeoLocation[];
  getSelectedGeoLocationId: (state: LocalLandingState) => string;
  getSelectedLocation: (state: LocalLandingState) => GeoLocation | null;
  getPropertyType: (state: LocalLandingState) => PropertyType;
  getPriceData: (state: LocalLandingState) => PriceData;
  getIsResolvingLocation: (state: LocalLandingState) => boolean;
  getIsPriceDataLoading: (state: LocalLandingState) => boolean;
  getHasResolveErrors: (state: LocalLandingState) => boolean;
}

interface LocalLandingMutations extends MutationTree<LocalLandingState> {
  setRouteLocation: (state: LocalLandingState, routeLocation: LocalLandingLocations) => void;
  setPropertyType: (state: LocalLandingState, propertyType: PropertyType) => void;
  setGeoLocations: (state: LocalLandingState, geoLocations: GeoLocation[]) => void;
  setSelectedGeoLocationId: (state: LocalLandingState, geoId: string) => void;
  setPriceData: (state: LocalLandingState, priceData: PriceData) => void;
  setIsResolvingLocation: (state: LocalLandingState, isResolvingLocation: boolean) => void;
  setIsPriceDataLoading: (state: LocalLandingState, isLoading: boolean) => void;
  setHasResolveErrors: (state: LocalLandingState, hasResolveErrors: boolean) => void;
}

interface LocalLandingActions extends ActionTree<LocalLandingState, AppState> {
  resolveLocationByUrl: (
    { commit }: ActionContext<LocalLandingState, AppState>,
    payload: { urlName: string; language: HgSupportedLanguage },
  ) => Promise<void>;
  resolveLocationByGeoLocation: (
    { commit }: ActionContext<LocalLandingState, AppState>,
    geoLocation: GeoLocation,
  ) => Promise<void>;
  resolveGeoLocations: (actionContext: ActionContext<LocalLandingState, AppState>) => Promise<void>;
  fetchPriceData: (actionContext: ActionContext<LocalLandingState, AppState>) => Promise<void>;
}

export const defaultPriceData: PriceData = {
  price: null,
  priceTrends: null,
};

export const getDefaultState = (): LocalLandingState => ({
  routeLocation: LocalLandingLocations.Zurich,
  geoLocations: [],
  selectedGeoLocationId: '',
  propertyType: PropertyType.House,
  priceData: defaultPriceData,
  isResolvingLocation: false,
  isPriceDataLoading: false,
  hasResolveErrors: false,
});

const state = (): LocalLandingState => getDefaultState();

const getters: LocalLandingGetters = {
  getRouteLocation: (state: LocalLandingState): LocalLandingLocations => state.routeLocation,
  getRouteGeoLocation: (state: LocalLandingState): GeoLocation | null => {
    const routeLocation = state.routeLocation;
    const geoId = routeLocationToGeoIdMapping[routeLocation];
    return state.geoLocations.find((geoLocation) => geoLocation.id === geoId) ?? null;
  },
  getGeoLocations: (state: LocalLandingState): GeoLocation[] => state.geoLocations,
  getSelectedGeoLocationId: (state: LocalLandingState): string => state.selectedGeoLocationId,
  getSelectedLocation: (state: LocalLandingState): GeoLocation | null => {
    return (
      state.geoLocations.find((geoLocation) => geoLocation.id === state.selectedGeoLocationId) ??
      null
    );
  },
  getPropertyType: (state: LocalLandingState): PropertyType => state.propertyType,
  getPriceData: (state: LocalLandingState): PriceData => state.priceData,
  getIsResolvingLocation: (state: LocalLandingState): boolean => state.isResolvingLocation,
  getIsPriceDataLoading: (state: LocalLandingState): boolean => state.isPriceDataLoading,
  getHasResolveErrors: (state: LocalLandingState): boolean => state.hasResolveErrors,
};

const mutations: LocalLandingMutations = {
  setRouteLocation(state: LocalLandingState, routeLocation: LocalLandingLocations): void {
    state.routeLocation = routeLocation;
  },
  setPropertyType(state: LocalLandingState, propertyType: PropertyType): void {
    state.propertyType = propertyType;
  },
  setGeoLocations(state: LocalLandingState, geoLocations: GeoLocation[]): void {
    state.geoLocations = geoLocations;
  },
  setSelectedGeoLocationId(state: LocalLandingState, geoId: string): void {
    state.selectedGeoLocationId = geoId;
  },
  setPriceData(state: LocalLandingState, priceData: PriceData): void {
    state.priceData = priceData;
  },
  setIsResolvingLocation(state: LocalLandingState, isResolvingLocation: boolean): void {
    state.isResolvingLocation = isResolvingLocation;
  },
  setIsPriceDataLoading(state: LocalLandingState, isLoading: boolean): void {
    state.isPriceDataLoading = isLoading;
  },
  setHasResolveErrors(state: LocalLandingState, hasResolveErrors: boolean): void {
    state.hasResolveErrors = hasResolveErrors;
  },
};

const actions: LocalLandingActions = {
  resolveLocationByUrl: async (
    context: ActionContext<LocalLandingState, AppState>,
    { urlName, language }: { urlName: string; language: HgSupportedLanguage },
  ): Promise<void> => {
    context.commit('setIsResolvingLocation', true);
    const location = await getGeoLocationByUrlName(urlName, language);
    const targetGeoIds = Object.values(routeLocationToGeoIdMapping);

    if (!location || !targetGeoIds.includes(location.id)) {
      context.commit('setHasResolveErrors', true);
      context.commit('setIsResolvingLocation', false);
      return;
    }

    const targetName = geoIdToRouteLocationMapping[location.id];
    context.commit('setRouteLocation', targetName);
    await context.dispatch('resolveGeoLocations');
    context.commit('setIsResolvingLocation', false);

    await context.dispatch('fetchPriceData');
  },

  resolveLocationByGeoLocation: async (
    context: ActionContext<LocalLandingState, AppState>,
    geoLocation: GeoLocation,
  ): Promise<void> => {
    const targetGeoIds = Object.values(routeLocationToGeoIdMapping);
    if (!targetGeoIds.includes(geoLocation.id)) {
      return;
    }
    context.commit('setIsResolvingLocation', true);
    const targetName = geoIdToRouteLocationMapping[geoLocation.id];
    context.commit('setRouteLocation', targetName);
    await context.dispatch('resolveGeoLocations');
    context.commit('setIsResolvingLocation', false);

    await context.dispatch('fetchPriceData');
  },

  resolveGeoLocations: async (
    context: ActionContext<LocalLandingState, AppState>,
  ): Promise<void> => {
    const routeLocation = context.state.routeLocation;
    const geoLocationIds = locationDropdownGeoIds[routeLocation];
    const geoLocations = await fetchGeoLocationsByIds(geoLocationIds);
    context.commit('setGeoLocations', geoLocations);
    context.commit('setSelectedGeoLocationId', geoLocationIds[0]); // Set the city/municipality one as default
  },

  fetchPriceData: async (context: ActionContext<LocalLandingState, AppState>): Promise<void> => {
    const selectedLocation: GeoLocation | null = context.getters.getSelectedLocation;
    if (!selectedLocation) {
      return;
    }

    const level = selectedLocation.iazi?.level;
    const levelId = selectedLocation.iazi?.id;
    if (!level || !levelId) {
      captureAction('No iazi level and id found', {
        geoId: selectedLocation?.id ?? '',
      });
      context.commit('setPriceData', {
        price: null,
        priceTrends: null,
      });
      context.commit('setIsPriceDataLoading', false);
      return;
    }

    context.commit('setIsPriceDataLoading', true);
    const [price, priceTrends] = await Promise.all([
      getPriceValue({ level, levelId }),
      getPriceEvolution({ level, levelId }),
    ]);
    context.commit('setPriceData', {
      price,
      priceTrends,
    });
    context.commit('setIsPriceDataLoading', false);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
} as LocalLandingModule;
